const listeners = new Set<() => void>();
const pending = new Map<string, string | null>();

let scheduled = false;

/**
 * The window the popstate listener is on, not a boolean.
 *
 * A boolean says "already attached" for whatever window was current the first
 * time anyone subscribed, and a browser only ever has one. A suite that swaps
 * `globalThis.window` between files does not, and the second window would
 * silently never be listened to.
 */
let attachedTo: unknown = null;

const notify = () => {
  for (const listener of listeners) listener();
};

export const readParam = (param: string): string | undefined => {
  if (typeof location === 'undefined') return undefined;
  return new URLSearchParams(location.search).get(param) ?? undefined;
};

export const flushUrl = (): void => {
  scheduled = false;
  if (pending.size === 0) return;
  const changes = [...pending.entries()];
  pending.clear();
  if (typeof location === 'undefined' || typeof history === 'undefined') return;
  const params = new URLSearchParams(location.search);
  for (const [param, raw] of changes) {
    if (raw === null) params.delete(param);
    else params.set(param, raw);
  }
  const search = params.toString();
  history.replaceState(history.state, '', `${location.pathname}${search ? `?${search}` : ''}${location.hash}`);
  notify();
};

const attach = () => {
  if (typeof window === 'undefined' || attachedTo === window) return;
  attachedTo = window;
  window.addEventListener('popstate', notify);
};

export const queueParam = (param: string, raw: string | null): void => {
  pending.set(param, raw);
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(flushUrl);
};

export const subscribeToUrl = (listener: () => void): (() => void) => {
  attach();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
